import { useEffect, useState } from "react";
import type {
  BaselineData,
  ExecutionData,
  Technician,
  AllocationItem,
} from "../types";

const DEFAULT_BASELINE: BaselineData = {
  projectName: "Proyek Baru",
  kickoff: "",
  deadline: "",
  milestones: [{ id: 1, label: "cth: Serah Terima Kunci", date: "", weight: 100 }],
  tasks: [
    { id: 1, milestone_id: 1, name: "cth: Pekerjaan Utama 1", weight: 50 },
    { id: 2, milestone_id: 1, name: "cth: Pekerjaan Utama 2", weight: 50 },
  ],
  budget: 1500000000,
  allocation: [
    { id: "sdm", name: "SDM / Tim Lapangan", percentage: 40, isDefault: true },
    { id: "material", name: "Material & Sparepart", percentage: 50, isDefault: true },
    { id: "contingency", name: "Kontingensi", percentage: 10, isDefault: true },
  ],
  teamMembers: [],
  vendors: [{ id: 1, name: "Vendor A", task: "Supply Bahan", status: "Pending" }],
  inventory: [{ id: 1, name: "Alat Utama", status: "Ready" }],
  global_checklists: [],
  payment_type: "full",
  termijns: [],
};

const DEFAULT_EXECUTION: ExecutionData = {
  completedTasks: [],
  taskProgress: {},
  actualExpenses: [],
  contingency: null,
  contingencyExpenses: [],
  usedInventoryIds: [],
  usedVendorIds: [],
  validatedTasks: [],
};

export function useProjectState(activeProjectQuery: any) {
  const [baselineData, setBaselineData] = useState<BaselineData>({ ...DEFAULT_BASELINE });
  const [executionData, setExecutionData] = useState<ExecutionData>({ ...DEFAULT_EXECUTION });
  const [taskTeams, setTaskTeams] = useState<Record<string, Technician[]>>({});
  const [managerNote, setManagerNote] = useState("");

  useEffect(() => {
    const proj = activeProjectQuery.data;
    if (!proj) return;

    const mappedBaseline: BaselineData = {
      projectName: proj.name,
      kickoff: proj.kickoff_date || "",
      deadline: proj.deadline_date || "",
      budget: proj.budget || 0,
      progress: proj.progress || 0,
      status: proj.status,
      allocation:
        proj.budget_allocations && Array.isArray(proj.budget_allocations)
          ? proj.budget_allocations
          : [
              { id: "sdm", name: "SDM / Tim Lapangan", percentage: proj.allocation_sdm || 0, isDefault: true },
              { id: "material", name: "Material & Sparepart", percentage: proj.allocation_material || 0, isDefault: true },
              { id: "contingency", name: "Kontingensi", percentage: proj.allocation_contingency || 0, isDefault: true },
            ],
      milestones: (proj.milestones || []).map((m: any) => ({
        id: m.id,
        label: m.label,
        date: m.date,
        weight: m.weight,
      })),
      tasks: (proj.milestones || []).flatMap((m: any) =>
        (m.tasks || []).map((t: any) => ({
          id: t.id,
          milestone_id: m.id,
          name: t.name,
          weight: t.weight,
          evidence: (() => {
            const raw = Array.isArray(t.evidence_media)
              ? t.evidence_media
              : Array.isArray(t.evidence_photos)
                ? t.evidence_photos
                : typeof t.evidence_photos === "string"
                  ? (() => {
                      try {
                        const parsed = JSON.parse(t.evidence_photos);
                        return Array.isArray(parsed) ? parsed : [];
                      } catch {
                        return [];
                      }
                    })()
                  : [];

            return raw
              .map((item: any) => {
                const looksVideo = (value: any) => {
                  const type = String(value?.type || "").toLowerCase();
                  if (type === "video") return true;
                  if (type === "photo" || type === "image") return false;
                  const mime = String(value?.mime || "").toLowerCase();
                  if (mime.startsWith("video/")) return true;
                  const haystack =
                    typeof value === "string"
                      ? value
                      : `${value?.url || ""} ${value?.path || ""} ${value?.image || ""}`;
                  return /\.(mp4|mov|m4v|webm|mkv)(\?|#|$)/i.test(haystack);
                };

                if (typeof item === "string") {
                  return {
                    image: item,
                    url: null,
                    type: looksVideo(item) ? "video" : "photo",
                    timestamp: t.updated_at,
                  };
                }
                if (item && typeof item === "object") {
                  const path = item.path || item.image || "";
                  if (!path && !item.url) return null;
                  return {
                    image: path || item.url,
                    url: item.url || null,
                    type: looksVideo(item) ? "video" : "photo",
                    mime: item.mime || null,
                    duration_seconds: item.duration_seconds ?? null,
                    timestamp: t.updated_at,
                  };
                }
                return null;
              })
              .filter(Boolean);
          })(),
          notes: t.notes || "",
          status: t.status,
          progress: t.progress || 0,
          rejection_reason: t.rejection_reason || null,
          validation_results: t.validation_results || null,
          completed_otp: t.completed_otp || null,
          assignments: t.assignments || [],
          checklists: proj.global_validation_checklists || [],
          createdAt: t.created_at,
          completedAt: t.updated_at,
        })),
      ),
      teamMembers: proj.team_members || proj.teamMembers || [],
      vendors: (proj.resources || [])
        .filter((r: any) => r.type === "vendor")
        .map((v: any) => ({
          id: v.id,
          name: v.name,
          task: v.description,
          cost: v.estimated_cost,
          status: v.status,
          vendor_id: v.vendor_id,
        })),
      inventory: (proj.resources || [])
        .filter((r: any) => r.type === "inventory")
        .map((i: any) => ({
          id: i.id,
          name: i.name,
          status: i.status,
        })),
      global_checklists: proj.global_validation_checklists || [],
      payment_type: proj.payment_type || "full",
      termijns: proj.termijns || [],
    };

    const taskProgressMap: Record<string, number> = {};
    (proj.milestones || []).forEach((m: any) => {
      (m.tasks || []).forEach((t: any) => {
        taskProgressMap[String(t.id)] = t.status === "completed" ? 100 : Number(t.progress || 0);
      });
    });

    const mappedExecution: ExecutionData = {
      completedTasks: (proj.milestones || []).flatMap((m: any) =>
        (m.tasks || []).filter((t: any) => t.status === "completed").map((t: any) => t.id),
      ),
      taskProgress: taskProgressMap,
      actualExpenses: proj.expenses || [],
      contingency: proj.contingency || null,
      contingencyExpenses: proj.contingency_expenses || [],
      usedInventoryIds: (proj.resources || [])
        .filter((r: any) => r.type === "inventory" && r.is_used)
        .map((r: any) => r.id),
      usedVendorIds: (proj.resources || [])
        .filter((r: any) => r.type === "vendor" && r.is_used)
        .map((r: any) => r.id),
      validatedTasks: (proj.milestones || []).flatMap((m: any) =>
        (m.tasks || []).filter((t: any) => t.status === "completed").map((t: any) => t.id),
      ),
    };

    const mappedTeams: Record<string, Technician[]> = {};
    (proj.milestones || []).forEach((m: any) => {
      (m.tasks || []).forEach((t: any) => {
        if (t.assignments) mappedTeams[String(t.id)] = t.assignments;
      });
    });

    setBaselineData(mappedBaseline);
    setExecutionData(mappedExecution);
    setTaskTeams(mappedTeams);
    setManagerNote(proj.manager_note || "");
  }, [activeProjectQuery.data]);

  const getDefaultBaseline = () => ({ ...DEFAULT_BASELINE });

  return {
    baselineData,
    setBaselineData,
    executionData,
    setExecutionData,
    taskTeams,
    setTaskTeams,
    managerNote,
    setManagerNote,
    getDefaultBaseline,
  };
}
